import { motion } from "framer-motion";
import { StatusBadge, EASE } from "./shared";
import { Route, TrainFront, TramFront, CableCar, Zap, Train, Bus } from "lucide-react";

const ICONS = { Route, TrainFront, TramFront, CableCar, Zap, Train, Bus };

function MiniLine({ stops, dashed }) {
  const n = stops.length;
  const w = 300;
  const x = (i) => 16 + (i * (w - 32)) / (n - 1);
  return (
    <svg viewBox={`0 0 ${w} 58`} className="w-full h-auto" aria-hidden="true">
      <line x1={x(0)} y1="18" x2={x(n - 1)} y2="18" stroke="#D4AF37" strokeOpacity="0.7" strokeWidth="1.6" strokeDasharray={dashed ? "5 5" : "0"} />
      {stops.map((s, i) => (
        <g key={s}>
          <circle cx={x(i)} cy="18" r={i === n - 1 ? 5 : 3.5} fill={i === n - 1 ? "#D4AF37" : "#0A0A0A"} stroke="#D4AF37" strokeWidth="1.3" />
          <text x={x(i)} y={i % 2 === 0 ? 40 : 52} textAnchor="middle" fill="#71717A" fontSize="7.5" letterSpacing="0.6" style={{ fontFamily: "Manrope" }}>{s}</text>
        </g>
      ))}
    </svg>
  );
}

export function MobilityModeCard({ mode, active, onSelect, delay = 0 }) {
  const Icon = ICONS[mode.icon];
  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.8, delay, ease: EASE }}
      onClick={onSelect}
      data-testid={`mobility-card-${mode.id}`}
      data-active={active}
      className={`card-lux p-6 rounded-[2px] flex flex-col cursor-pointer transition-colors ${active ? "border-[#D4AF37]/60 bg-[#D4AF37]/[0.05]" : "hover:border-[#D4AF37]/35"}`}
    >
      <div className="mb-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <span className="w-10 h-10 grid place-items-center border border-[#D4AF37]/30 rounded-[2px] text-[#D4AF37]"><Icon size={18} /></span>
          <StatusBadge status={mode.status} className="shrink-0" />
        </div>
        <p className="font-serif-lux text-xl text-[#F5F5F0] leading-tight">{mode.mode}</p>
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/80 mt-1">{mode.stat}</p>
      </div>

      <div className="border border-[#D4AF37]/10 bg-[#0A0A0A] rounded-[2px] px-2 pt-1 mb-4" data-testid={`mobility-card-${mode.id}-minimap`}>
        <MiniLine stops={mode.stops} dashed={mode.dashed} />
      </div>

      <p className="text-[13px] text-[#F5F5F0]/90 leading-relaxed mb-3">{mode.headline}</p>
      <ul className="space-y-2 mb-4">
        {mode.facts.map((f) => (
          <li key={f} className="flex gap-2 text-[12px] text-[#A1A1AA] leading-relaxed">
            <span className="mt-[7px] w-1 h-1 rounded-full bg-[#D4AF37] shrink-0" />{f}
          </li>
        ))}
      </ul>
      <div className="mt-auto pt-4 border-t border-[#D4AF37]/10">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1.5">What it means for you</p>
        <p className="text-[12px] text-[#A1A1AA] leading-relaxed">{mode.benefit}</p>
      </div>
    </motion.article>
  );
}
